// Core
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  useNavigate,
  useSearchParams,
  NavigateFunction,
} from 'react-router-dom';

// Libraries
import _ from 'lodash';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';

// Plugins
import {
  requestVantageApi,
  RequestMethod,
  isHTTPResponseError,
} from '../../plugins/request';

// Hooks
import { useBreakpoint } from '../../hooks/useBreakpoint';

// Store
import {
  setAuthToken,
  setUser,
} from '../../store/session.store';

// Types
import {
  LoginAuthResponse,
  User,
  UserType,
} from '../../types/global';

// Layout
import { Alert, AlertType } from '../../layout/elements/Alert';
import { AlertLine } from '../../layout/icons';
import { Button, ButtonSize } from '../../layout/elements/Button';
import { Checkbox, CheckboxSize } from '../../layout/elements/Checkbox';
import { Input, InputType } from '../../layout/elements/Input';
import { LeftPanelImageRight } from '../../layout/LeftPanelImageRight';
import {
  LinkTo,
  LinkToColor,
  LinkToType,
  LinkToWeight,
} from '../../layout/elements/LinkTo';
import { Paragraph, ParagraphAlign } from '../../layout/elements/Paragraph';
import {
  Title,
  TitleAlign,
  TitleSize,
  TitleWeight,
} from '../../layout/elements/Title';
import { showToast, ToastType } from '../../layout/elements/Toast';

// Style
import {
  AlertContainer,
  Description,
  FormContentContainer,
  InputEmail,
  LoginContentContainer,
  OptionsLoginContainer,
  RegisteredTrademark,
  TitleContainer,
} from './Login.style';

function Login(): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const inviteToken: string | null = searchParams.get('token');
  const errorFromUrl: string | null = searchParams.get('error');
  const { isXs, isSm, isMd } = useBreakpoint();

  /* ***********************************************************************************************
  ***************************************** LOCAL STATES *******************************************
  *********************************************************************************************** */

  interface LoginFormState {
    email: string | null;
    error: string | null;
    password: string | null;
    rememberMe: boolean;
  }

  const initialLoginFormState: LoginFormState = {
    email: null,
    error: null,
    password: null,
    rememberMe: false,
  };

  const [loginFormState, setLoginFormState] = useState<LoginFormState>(initialLoginFormState);

  /* ************************************* LOGIN LOADING ************************************** */
  const [loginButtonLoading, setLoginButtonLoading] = useState<number | null | true>(null);

  /* ********************************* TOKEN REDIRECT CONTROL *********************************** */
  const hasRedirectedRef: React.MutableRefObject<boolean> = useRef<boolean>(false);

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  /**
   * Redirects to RegisterPassword when token is present in URL
   * Token validation will be done in RegisterPassword component
   */
  const handleTokenRedirect = useCallback((): void => {
    if (inviteToken && !hasRedirectedRef.current) {
      hasRedirectedRef.current = true;

      // Redirect to RegisterPassword with token
      navigate(`/register-password?token=${inviteToken}`);
    }
  }, [inviteToken, navigate]);

  // Function to handle errors
  const handleLoginFormErrors = (error: unknown): void => {
    let errorMessage = t('common.defaultError');

    // Check if the error is an HTTP response error
    if (isHTTPResponseError(error)) {
      if (error.message.includes('Network Error')) {
        errorMessage = t('common.networkError');
      } else if (error.response.status === 401) {
        errorMessage = t('view.login.invalidUserOrPassword');
      }
    }

    setLoginFormState({ ...loginFormState, error: errorMessage });
  };

  /**
  * Submits the login form data to the authentication endpoint.
  *
  * @returns {Promise<void>} A promise that resolves when the login process is complete.
  */
  const submitLoginForm = async (): Promise<void> => {
    setLoginButtonLoading(true);

    try {
      const authResponse: LoginAuthResponse = await requestVantageApi.users({
        method: RequestMethod.POST,
        path: '/auth/authenticate',
        body: {
          email: loginFormState.email,
          password: loginFormState.password,
        },
        handleErrors: false,
      });

      const { token } = authResponse;

      const decodedToken: { user: string } = jwtDecode(token);
      const decodedUser: User = JSON.parse(decodedToken.user);
      setUser(decodedUser);

      setAuthToken(token);

      if (decodedUser.type === UserType.admin || decodedUser.type === UserType.service) {
        navigate('/');
      } else if (decodedUser.type === UserType.customer) {
        navigate('/plant-management');
      }
    } catch (error: unknown) {
      setLoginButtonLoading(null);
      handleLoginFormErrors(error);
    }
  };

  /**
  * Toggles the "Remember Me" checkbox state.
  *
  * This function updates the `rememberMe` property in the `loginFormState` object
  * by setting it to the opposite of its current value.
  */
  const toggleRememberMe = (): void => {
    setLoginFormState((prevState: LoginFormState) => ({
      ...prevState,
      rememberMe: !prevState.rememberMe,
    }));
  };

  /**
  * Updates a specific field in the login form state.
  *
  * @param {string} field - The name of the field to update.
  * @param {string | null | number} [value] - The new value for the field. Defaults to undefined.
  * @returns {void}
  */
  const updateLoginField = (field: keyof LoginFormState, value?: string | null | number): void => {
    setLoginFormState((prevState: LoginFormState) => ({
      ...prevState,
      [field]: value as string | null,
      error: null,
    }));
  };

  /**
  * Determines if the login form submit button should be disabled.
  * The button is disabled if both the email and password fields are null or undefined.
  * @type {boolean}
  */
  const isDisabledToSubmitLoginForm: boolean = (
    _.isNil(loginFormState.email) || _.isNil(loginFormState.password)
  );

  /* ***********************************************************************************************
  ************************************* COMPONENT HANDLING *****************************************
  *********************************************************************************************** */

  // Check for token in URL and redirect to RegisterPassword
  useEffect(() => {
    handleTokenRedirect();
  }, [handleTokenRedirect]);

  // Show toast if there's an error from RegisterPassword redirect
  useEffect(() => {
    if (errorFromUrl) {
      let errorMessage: string = t('view.registerPassword.invalidTokenError');

      if (errorFromUrl === 'invalidToken') {
        errorMessage = t('view.registerPassword.invalidTokenError');
      } else if (errorFromUrl === 'tokenNotFound') {
        errorMessage = t('view.registerPassword.tokenNotFoundError');
      } else if (errorFromUrl === 'tokenExpired') {
        errorMessage = t('view.registerPassword.tokenExpiredError');
      }

      showToast({
        text: errorMessage,
        type: ToastType.error,
      });

      // Remove error parameter from URL
      const newSearchParams: URLSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [errorFromUrl, t, searchParams, setSearchParams]);

  return (
    <LeftPanelImageRight>
      <LoginContentContainer data-testid="login-content-container">
        <FormContentContainer data-testid="form-content-container">

          <TitleContainer data-testid="title-container">
            <Title
              align={TitleAlign.center}
              size={(isXs || isSm || isMd) ? TitleSize.xxs : TitleSize.xs}
              weight={TitleWeight.bold}
            >
              {t('view.login.title').split('{trademark}')[0]}
              <RegisteredTrademark>®</RegisteredTrademark>
              {t('view.login.title').split('{trademark}')[1]}
            </Title>
          </TitleContainer>

          <Description data-testid="description">
            <Paragraph align={ParagraphAlign.center}>
              {t('view.login.instructionsParagraph')}
            </Paragraph>
          </Description>

          {loginFormState.error && (
            <AlertContainer data-testid="alert-container">
              <Alert
                closable={false}
                description={loginFormState.error}
                icon={<AlertLine />}
                testId="errorAlert"
                type={AlertType.error}
              />
            </AlertContainer>
          )}

          <InputEmail data-testid="input-email-container">
            <Input
              error={loginFormState.error !== null}
              label={`${t('common.email')}`}
              onChange={(value?: string | number | null) => updateLoginField('email', value)}
              testId="input-email"
              value={loginFormState.email}
              onPressEnter={() => {
                const inputs = Array.from(document.querySelectorAll('input'))[1];
                if (inputs) inputs.focus();
              }}
            />
          </InputEmail>

          <Input
            error={loginFormState.error !== null}
            label={t('common.password') || ''}
            onChange={(value?: string | number | null) => updateLoginField('password', value)}
            testId="input-password"
            type={InputType.password}
            value={loginFormState.password}
            onPressEnter={!isDisabledToSubmitLoginForm ? submitLoginForm : undefined}
          />

          <OptionsLoginContainer>
            <Checkbox
              checked={loginFormState.rememberMe}
              onChange={toggleRememberMe}
              size={CheckboxSize.sm}
              text={`${t('view.login.rememberMeCheckbox')}`}
            />
            <LinkTo
              color={LinkToColor.primary}
              target="/password-recovery"
              type={LinkToType.link}
              weight={LinkToWeight.medium}
            >
              {t('view.login.forgotPasswordLinkTo')}
            </LinkTo>
          </OptionsLoginContainer>

          <Button
            size={ButtonSize.lg}
            loading={loginButtonLoading}
            disabled={isDisabledToSubmitLoginForm}
            loadingText={`${t('view.login.signingIn')}`}
            onClick={submitLoginForm}
            testId="signing-in-button"
          >
            {t('view.login.signIn')}
          </Button>

        </FormContentContainer>
      </LoginContentContainer>
    </LeftPanelImageRight>
  );
}

export { Login };
